import Analytics from '@/utils/analytics';
import DeleteIcon from '@assets/icons/delete.svg';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { FilterButton, FilterType } from './filter-button';
import { FilteredCategoryItemGroup } from '@/contexts/item-context-types';
import { findItemGroupPath } from '@/utils/category';
import {
  FlatList,
  PixelRatio,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { FONTS, HEADER_PADDING_HORIZONTAL_SMALL, TYPOGRAPHY } from '@/styles';
import { HorizontalListArrows } from './horizontal-list-arrows';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';

export const LIST_HEIGHT = 42 * PixelRatio.getFontScale();

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  data: FilteredCategoryItemGroup[];
  onItemGroupPress: (itemGroup: FilteredCategoryItemGroup | null) => void;
  selectedItemGroup: FilteredCategoryItemGroup | null;
  style?: StyleProp<ViewStyle>;
  trackingKey: string;
}

export const ItemGroupSelector = ({
  containerStyle,
  contentContainerStyle,
  data,
  onItemGroupPress,
  selectedItemGroup,
  style,
  trackingKey,
}: Props) => {
  const theme = useTheme();
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  const [groupPath, setGroupPath] = useState<FilteredCategoryItemGroup[]>([]);

  const listRef = useRef<FlatList<FilteredCategoryItemGroup> | null>(null);

  useLayoutEffect(() => {
    if (selectedItemGroup) {
      const path = findItemGroupPath(data, selectedItemGroup);
      if (path) {
        setGroupPath(path);
      } else {
        setGroupPath([]);
      }
    } else {
      setGroupPath([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemGroupClick = (itemGroup: FilteredCategoryItemGroup) => {
    if (
      itemGroup.directSubcategories &&
      itemGroup.directSubcategories.length > 0
    ) {
      setGroupPath([...groupPath, itemGroup]);
    }
    onItemGroupPress(itemGroup);
  };

  const handleCloseButtonClick = () => {
    setGroupPath(groupPath.slice(0, -1));
    const parentGroup = groupPath[groupPath.length - 2];
    if (parentGroup) {
      onItemGroupPress(parentGroup);
    } else {
      onItemGroupPress(null);
    }
  };

  const listContentContainerStyle = useSmallLayoutSizeValue<
    StyleProp<ViewStyle>
  >(
    [styles.listContentContainer, contentContainerStyle],
    [
      styles.listContentContainer,
      styles.listContentContainerLarge,
      contentContainerStyle,
    ],
  );

  const itemGroups =
    groupPath.length > 0
      ? groupPath[groupPath.length - 1].directSubcategories
      : data;

  return data.length > 0 ? (
    <View style={styles.row}>
      {groupPath.length > 0 && (
        <View style={styles.closeButtonContainer}>
          <TouchableOpacity
            onPress={handleCloseButtonClick}
            style={styles.closeButton}
            testID="horizontal-group-close-button"
          >
            <Text
              numberOfLines={1}
              style={[
                styles.closeButtonText,
                isSmallLayout
                  ? styles.closeButtonTextTruncated
                  : styles.closeButtonTextTruncatedLarge,
              ]}
            >
              {groupPath[groupPath.length - 1].name}
            </Text>
            <View style={styles.iconContainer}>
              <DeleteIcon
                fill={theme.colors.inactiveText}
                height={6}
                width={6}
              />
            </View>
          </TouchableOpacity>
        </View>
      )}
      <View
        style={[
          styles.relativeContainer,
          containerStyle,
          !isSmallLayout && styles.containerStyleLarge,
        ]}
      >
        <HorizontalListArrows<FilteredCategoryItemGroup>
          trackingKey={trackingKey}
        >
          {(listProps, arrowListRef) => (
            <FlatList<FilteredCategoryItemGroup>
              {...listProps}
              contentContainerStyle={listContentContainerStyle}
              data={itemGroups}
              horizontal
              keyExtractor={(item) => item.uuid!}
              onScrollToIndexFailed={() => {
                // prevents errors if scrolling before being measured
              }}
              ref={(ref) => {
                // attach both local and arrow list refs
                listRef.current = ref;
                arrowListRef.current = ref;
              }}
              renderItem={({ item, index }) => (
                <FilterButton
                  filterType={FilterType.BUTTON}
                  isSelected={
                    selectedItemGroup
                      ? selectedItemGroup.path === item.path
                      : index === 0
                  }
                  key={item.uuid}
                  onPress={() => {
                    if (item.uuid === selectedItemGroup?.uuid) {
                      handleCloseButtonClick();
                    } else {
                      Analytics.track(`${trackingKey}.button.press`, {
                        item: item.name,
                      });
                      handleItemGroupClick(item);
                    }
                  }}
                  style={styles.filterButtonBaseStyle}
                  text={item.name}
                />
              )}
              showsHorizontalScrollIndicator={false}
              style={[styles.base, style]}
            />
          )}
        </HorizontalListArrows>
      </View>
    </View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.white,
  },
  closeButton: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVetical: 4,
  },
  closeButtonContainer: {
    backgroundColor: theme.colors.divider,
    justifyContent: 'center',
  },
  closeButtonText: {
    ...TYPOGRAPHY.small,
    ...FONTS.primarySemiBold,
    marginRight: 10,
  },
  closeButtonTextTruncated: {
    maxWidth: 100,
  },
  closeButtonTextTruncatedLarge: {
    maxWidth: 240,
  },
  containerStyleLarge: {
    paddingLeft: HEADER_PADDING_HORIZONTAL_SMALL,
  },
  filterButtonBaseStyle: {
    backgroundColor: theme.colors.divider,
    borderWidth: 0,
  },
  iconContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    height: 16,
    justifyContent: 'center',
    width: 16,
  },
  listContentContainer: {
    alignItems: 'center',
    height: LIST_HEIGHT,
    paddingLeft: HEADER_PADDING_HORIZONTAL_SMALL,
    paddingRight: HEADER_PADDING_HORIZONTAL_SMALL / 2,
  },
  listContentContainerLarge: {
    paddingLeft: 0,
  },
  relativeContainer: {
    flex: 1,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
  },
}));
